import type { AssignedRiskApplicationCoverageInformationViewModel } from 'src/api/types/AssignedRiskApplicationCoverageInformationViewModel';

import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import Skeleton from '@mui/material/Skeleton';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TableContainer from '@mui/material/TableContainer';

import { MESSAGES } from 'src/constants/messages';
import { useAssignedRiskApplicationCoverageInformation } from 'src/api/hooks/useAssignedRiskApplicationQueries';

import { formatDate } from '../../shared/utils/policyUtils';

type CoverageInformationProps = Readonly<{ assignedRiskAppId: number }>;
const coverageMessages = MESSAGES.ASSIGNED_RISK_APPLICATION.COVERAGE_INFORMATION;

function displayValue(value: string | number | null | undefined): string | number {
  return value ?? '';
}

function Field({ label, fieldValue }: Readonly<{ label: string; fieldValue: string | number | null | undefined }>) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle2">{displayValue(fieldValue)}</Typography>
    </Grid>
  );
}

function CoverageRegion({ coverage }: Readonly<{ coverage: AssignedRiskApplicationCoverageInformationViewModel }>) {
  return (
    <Card data-testid="coverage-information-card">
      <CardContent>
        <Typography variant="h5">{coverageMessages.TITLE}</Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Field label={coverageMessages.COMBO_ID_LABEL} fieldValue={coverage.comboId} />
          <Field label={coverageMessages.COVERAGE_ID_LABEL} fieldValue={coverage.coverageId} />
          <Field label={coverageMessages.FEIN_LABEL} fieldValue={coverage.feinLastFive} />
          <Field label={coverageMessages.COVERAGE_NAME_LABEL} fieldValue={coverage.coverageName} />
          <Field label={coverageMessages.DOING_BUSINESS_AS_LABEL} fieldValue={coverage.doingBusinessAs} />
          <Field label={coverageMessages.LEGAL_STATUS_LABEL} fieldValue={coverage.legalNatureDescription} />
          <Field label={coverageMessages.YEARS_IN_BUSINESS_LABEL} fieldValue={coverage.yearsInBusiness} />
          <Field label={coverageMessages.LEGAL_STATUS_OTHER_LABEL} fieldValue={coverage.legalNatureOther} />
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">{coverageMessages.BUSINESS_OPERATIONS_LABEL}</Typography>
            <Typography variant="subtitle2">{displayValue(coverage.businessOperationsDescription)}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function ContactRegion({ coverage }: Readonly<{ coverage: AssignedRiskApplicationCoverageInformationViewModel }>) {
  const address = coverage.mailingAddress;
  return (
    <Card data-testid="coverage-information-contact-card">
      <CardContent>
        <Typography variant="h5">{coverageMessages.CONTACT_TITLE}</Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Field label={coverageMessages.MAILING_ADDRESS_LABEL} fieldValue={[address?.street, address?.city, address?.stateAbbreviation, address?.zipCode].filter(Boolean).join(', ')} />
          <Field label={coverageMessages.AREA_LABEL} fieldValue={address?.geographicArea} />
          <Field label={coverageMessages.PHONE_LABEL} fieldValue={coverage.contactPhoneNumber} />
          <Field label={coverageMessages.EMAIL_LABEL} fieldValue={coverage.contactEmail} />
        </Grid>
      </CardContent>
    </Card>
  );
}

function OwnersRegion({ owners }: Readonly<{ owners: AssignedRiskApplicationCoverageInformationViewModel['owners'] }>) {
  const displayOwners = owners ?? [];

  return (
    <Card data-testid="coverage-information-owners-card">
      <CardContent>
        <Typography variant="h5">{coverageMessages.OWNERS_TITLE}</Typography>
        {displayOwners.length === 0 ? (
          <Alert severity="info" data-testid="coverage-information-no-owners">{coverageMessages.NO_OWNERS}</Alert>
        ) : (
          <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>{coverageMessages.OWNER_HEADERS.map((header) => <TableCell key={header}>{header}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {displayOwners.map((owner) => <TableRow key={owner.assignedRiskAppOwnerId} data-testid="coverage-information-owner">
                  <TableCell><Typography variant="subtitle2">{displayValue(owner.name)}</Typography></TableCell>
                  <TableCell>{displayValue(owner.title)}</TableCell>
                  <TableCell>{getCoverageLabel(owner.electCoverageStatus)}</TableCell>
                  <TableCell>{displayValue(owner.salary)}</TableCell>
                  <TableCell>{displayValue(owner.ownershipPercent)}</TableCell>
                  <TableCell>{formatDate(owner.birthDate)}</TableCell>
                  <TableCell>{displayValue(owner.classCode)}</TableCell>
                  <TableCell>{displayValue(owner.duties)}</TableCell>
                </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

function getCoverageLabel(electCoverageStatus: string | null | undefined): string {
  if (electCoverageStatus === 'accepted') {
    return coverageMessages.ACCEPT;
  }

  if (electCoverageStatus === 'rejected') {
    return coverageMessages.REJECT;
  }

  return '';
}

export default function CoverageInformation({ assignedRiskAppId }: CoverageInformationProps) {
  const { data, isLoading, isError } = useAssignedRiskApplicationCoverageInformation(assignedRiskAppId);
  const coverage = data?.data;

  if (isError) return <Alert severity="error" data-testid="coverage-information-error">{coverageMessages.LOAD_ERROR}</Alert>;
  if (isLoading) return <Skeleton variant="rectangular" height={360} data-testid="coverage-information-loading" />;
  if (!coverage) return <Alert severity="warning" data-testid="coverage-information-not-found">{coverageMessages.NOT_FOUND}</Alert>;

  return <Grid container spacing={3} sx={{ mt: 1 }} data-testid="coverage-information-content"><Grid size={{ xs: 12 }}><CoverageRegion coverage={coverage} /></Grid><Grid size={{ xs: 12 }}><ContactRegion coverage={coverage} /></Grid><Grid size={{ xs: 12 }}><OwnersRegion owners={coverage.owners ?? []} /></Grid></Grid>;
}
